import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  GraduationCap,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  ShieldCheck,
  Users,
  Trophy,
  Crown,
  Flame,
  Calendar,
  Target,
  BookOpen,
  ArrowUp,
  ArrowDown,
  FileText,
} from "lucide-react";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";

interface PepPrediction {
  userId: string;
  firstName: string;
  lastName: string;
  school: string | null;
  grade: number;
  overallReadiness: number;
  readinessLevel: "strong" | "on_track" | "at_risk" | "needs_support";
  subjectScores: Array<{
    subject: string;
    accuracy: number;
    totalQuestions: number;
    correctAnswers: number;
    coverage: number;
    readiness: number;
  }>;
  engagementScore: number;
  consistencyScore: number;
  recentTrend: "improving" | "stable" | "declining";
  lastActiveAt: string;
  totalXP: number;
  daysActive: number;
  pepWindowDaysAway: number | null;
  /** Where the student sits on the national ability scale (0-100), null until calibrated. */
  abilityPercentile: number | null;
  /** Calibrated chance of reaching Proficient (0-100), null until the predictor is calibrated on real PEP results. */
  proficiencyProbability: number | null;
  factors: {
    accuracy: number;
    volume: number;
    engagement: number;
    consistency: number;
    subjectCoverage: number;
  };
}

interface ChampionRecord {
  userId: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl: string | null;
  totalDaysAtTop: number;
  currentStreak: number;
  isCurrentChampion: boolean;
  longestStreak: number;
  termName: string;
}

interface ChampionResponse {
  currentChampion: ChampionRecord | null;
  champions: ChampionRecord[];
  termName: string;
  termProgress: number;
}

function getReadinessColor(level: string) {
  switch (level) {
    case "strong": return "text-green-600 dark:text-green-400";
    case "on_track": return "text-blue-600 dark:text-blue-400";
    case "at_risk": return "text-amber-600 dark:text-amber-400";
    case "needs_support": return "text-red-600 dark:text-red-400";
    default: return "text-muted-foreground";
  }
}

function getReadinessBg(level: string) {
  switch (level) {
    case "strong": return "bg-green-500/10";
    case "on_track": return "bg-blue-500/10";
    case "at_risk": return "bg-amber-500/10";
    case "needs_support": return "bg-red-500/10";
    default: return "bg-muted/50";
  }
}

function getReadinessLabel(level: string) {
  switch (level) {
    case "strong": return "Strong";
    case "on_track": return "On Track";
    case "at_risk": return "At Risk";
    case "needs_support": return "Needs Support";
    default: return level;
  }
}

function getReadinessProgressColor(level: string) {
  switch (level) {
    case "strong": return "[&>div]:bg-green-500";
    case "on_track": return "[&>div]:bg-blue-500";
    case "at_risk": return "[&>div]:bg-amber-500";
    case "needs_support": return "[&>div]:bg-red-500";
    default: return "";
  }
}

function TrendIcon({ trend }: { trend: string }) {
  switch (trend) {
    case "improving":
      return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
    case "declining":
      return <TrendingDown className="w-4 h-4 text-red-500 dark:text-red-400" />;
    default:
      return <Minus className="w-4 h-4 text-muted-foreground" />;
  }
}

export default function AdminPepPredictions() {
  const [filterLevel, setFilterLevel] = useState("all");
  const [sortBy, setSortBy] = useState<"readiness" | "engagement" | "accuracy">("readiness");

  const { data: predictions, isLoading: predictionsLoading } = useQuery<PepPrediction[]>({
    queryKey: ["/api/admin/pep-predictions"],
  });

  const { data: championData, isLoading: championsLoading } = useQuery<ChampionResponse>({
    queryKey: ["/api/admin/champion-tracking"],
  });

  const filteredPredictions = predictions
    ?.filter(p => filterLevel === "all" || p.readinessLevel === filterLevel)
    ?.sort((a, b) => {
      switch (sortBy) {
        case "engagement": return b.engagementScore - a.engagementScore;
        case "accuracy": return b.factors.accuracy - a.factors.accuracy;
        default: return b.overallReadiness - a.overallReadiness;
      }
    }) ?? [];

  const summaryStats = predictions ? {
    total: predictions.length,
    strong: predictions.filter(p => p.readinessLevel === "strong").length,
    onTrack: predictions.filter(p => p.readinessLevel === "on_track").length,
    atRisk: predictions.filter(p => p.readinessLevel === "at_risk").length,
    needsSupport: predictions.filter(p => p.readinessLevel === "needs_support").length,
    avgReadiness: Math.round(predictions.reduce((s, p) => s + p.overallReadiness, 0) / Math.max(1, predictions.length)),
    pepDaysAway: predictions[0]?.pepWindowDaysAway,
  } : null;

  if (predictionsLoading || championsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-primary" />
            PEP Readiness & Champions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Grade 6 exam readiness predictions and leaderboard champion tracking
          </p>
        </div>
        {summaryStats?.pepDaysAway && (
          <Badge variant="outline" className="gap-1.5 text-sm">
            <Calendar className="w-3.5 h-3.5" />
            PEP in {summaryStats.pepDaysAway} days
          </Badge>
        )}
      </div>

      <Tabs defaultValue="predictions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="predictions" className="gap-1.5">
            <Target className="w-3.5 h-3.5" />
            PEP Predictions
          </TabsTrigger>
          <TabsTrigger value="champions" className="gap-1.5">
            <Crown className="w-3.5 h-3.5" />
            Champion Tracker
          </TabsTrigger>
        </TabsList>

        <TabsContent value="predictions" className="space-y-4">
          {summaryStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Total G6</span>
                  </div>
                  <div className="text-2xl font-bold">{summaryStats.total}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-muted-foreground">Strong</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summaryStats.strong}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-muted-foreground">On Track</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{summaryStats.onTrack}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span className="text-xs text-muted-foreground">At Risk</span>
                  </div>
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{summaryStats.atRisk}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-muted-foreground">Needs Support</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{summaryStats.needsSupport}</div>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <CardTitle className="text-lg">Student Readiness</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={filterLevel} onValueChange={setFilterLevel}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="strong">Strong</SelectItem>
                      <SelectItem value="on_track">On Track</SelectItem>
                      <SelectItem value="at_risk">At Risk</SelectItem>
                      <SelectItem value="needs_support">Needs Support</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="readiness">Readiness</SelectItem>
                      <SelectItem value="engagement">Engagement</SelectItem>
                      <SelectItem value="accuracy">Accuracy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredPredictions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No grade 6 students found</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead className="text-center">Readiness</TableHead>
                        <TableHead className="text-center">National percentile</TableHead>
                        <TableHead className="text-center">Math</TableHead>
                        <TableHead className="text-center">English</TableHead>
                        <TableHead className="text-center">Science</TableHead>
                        <TableHead className="text-center">Engagement</TableHead>
                        <TableHead className="text-center">Trend</TableHead>
                        <TableHead className="text-center">Active</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPredictions.map((student) => {
                        const math = student.subjectScores.find(s => s.subject === "math");
                        const english = student.subjectScores.find(s => s.subject === "english");
                        const science = student.subjectScores.find(s => s.subject === "science");

                        return (
                          <TableRow key={student.userId}>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {student.firstName} {student.lastName}
                                </div>
                                {student.school && (
                                  <div className="text-xs text-muted-foreground">{student.school}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-flex flex-col items-center gap-1">
                                    <Badge
                                      variant="secondary"
                                      className={`${getReadinessBg(student.readinessLevel)} ${getReadinessColor(student.readinessLevel)} border-0`}
                                    >
                                      {student.overallReadiness}%
                                    </Badge>
                                    <span className={`text-[10px] ${getReadinessColor(student.readinessLevel)}`}>
                                      {getReadinessLabel(student.readinessLevel)}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="space-y-1 text-xs">
                                    <div>Accuracy: {student.factors.accuracy}%</div>
                                    <div>Volume: {student.factors.volume}%</div>
                                    <div>Engagement: {student.factors.engagement}%</div>
                                    <div>Consistency: {student.factors.consistency}%</div>
                                    <div>Subject Coverage: {student.factors.subjectCoverage}%</div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="inline-flex flex-col items-center gap-1">
                                <Badge variant="secondary" className="border-0">
                                  {student.abilityPercentile === null || student.abilityPercentile === undefined
                                    ? "n/a"
                                    : `${student.abilityPercentile}th`}
                                </Badge>
                                {student.proficiencyProbability !== null && student.proficiencyProbability !== undefined && (
                                  <span className="text-[10px] text-muted-foreground">
                                    P(Proficient) {student.proficiencyProbability}%
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <SubjectCell score={math} />
                            </TableCell>
                            <TableCell className="text-center">
                              <SubjectCell score={english} />
                            </TableCell>
                            <TableCell className="text-center">
                              <SubjectCell score={science} />
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <Progress
                                  value={student.engagementScore}
                                  className="h-1.5 w-12"
                                />
                                <span className="text-xs text-muted-foreground">
                                  {student.engagementScore}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <TrendIcon trend={student.recentTrend} />
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(student.lastActiveAt), { addSuffix: true })}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Link href={`/admin/user/${student.userId}/report`}>
                                <Button variant="ghost" size="icon">
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="champions" className="space-y-4">
          {championData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      Current #1 Champion
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {championData.currentChampion ? (
                      <div className="flex items-center gap-4">
                        <Avatar className="h-14 w-14 border-2 border-amber-500/30">
                          <AvatarImage src={championData.currentChampion.profilePhotoUrl || undefined} />
                          <AvatarFallback className="bg-amber-500/10 text-amber-600 text-lg font-bold">
                            {championData.currentChampion.firstName?.[0]}{championData.currentChampion.lastName?.[0] || ""}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-lg font-semibold">
                            {championData.currentChampion.firstName} {championData.currentChampion.lastName}
                          </div>
                          <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Flame className="w-3.5 h-3.5 text-orange-500" />
                              {championData.currentChampion.currentStreak} day streak
                            </span>
                            <span className="flex items-center gap-1">
                              <Trophy className="w-3.5 h-3.5 text-amber-500" />
                              {championData.currentChampion.totalDaysAtTop} days total
                            </span>
                            <span className="flex items-center gap-1">
                              <ArrowUp className="w-3.5 h-3.5 text-green-500" />
                              Best: {championData.currentChampion.longestStreak} days
                            </span>
                          </div>
                        </div>
                        <Link href={`/admin/user/${championData.currentChampion.userId}/report`}>
                          <Button variant="outline" size="sm">
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            Report
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No champion data available yet</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      {championData.termName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Term Progress</span>
                          <span className="font-medium">{championData.termProgress}%</span>
                        </div>
                        <Progress value={championData.termProgress} className="h-2" />
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {championData.champions.length} student{championData.champions.length !== 1 ? "s" : ""} have held #1 this term
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Champion History - {championData.termName}</CardTitle>
                </CardHeader>
                <CardContent>
                  {championData.champions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No leaderboard snapshot data yet. Champions are tracked from daily snapshots.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead className="text-center">Days at #1</TableHead>
                          <TableHead className="text-center">Current Streak</TableHead>
                          <TableHead className="text-center">Longest Streak</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {championData.champions.map((champ) => (
                          <TableRow key={champ.userId}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={champ.profilePhotoUrl || undefined} />
                                  <AvatarFallback className="text-xs">
                                    {champ.firstName?.[0]}{champ.lastName?.[0] || ""}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">
                                  {champ.firstName} {champ.lastName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {champ.totalDaysAtTop}
                            </TableCell>
                            <TableCell className="text-center">
                              {champ.currentStreak > 0 ? (
                                <span className="flex items-center justify-center gap-1 text-orange-500">
                                  <Flame className="w-3.5 h-3.5" />
                                  {champ.currentStreak}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">{champ.longestStreak}</TableCell>
                            <TableCell className="text-center">
                              {champ.isCurrentChampion ? (
                                <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0 gap-1">
                                  <Crown className="w-3 h-3" />
                                  #1
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">Former</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Link href={`/admin/user/${champ.userId}/report`}>
                                <Button variant="ghost" size="icon">
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SubjectCell({ score }: { score?: { accuracy: number; totalQuestions: number; readiness: number } }) {
  if (!score || score.totalQuestions === 0) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  const color = score.accuracy >= 70
    ? "text-green-600 dark:text-green-400"
    : score.accuracy >= 50
    ? "text-amber-600 dark:text-amber-400"
    : "text-red-600 dark:text-red-400";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-flex flex-col items-center">
          <span className={`text-sm font-medium ${color}`}>{score.accuracy}%</span>
          <span className="text-[10px] text-muted-foreground">{score.totalQuestions}q</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs">
          Readiness: {score.readiness}%
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
